/**
 * The catalog area's words (console redesign, docs/console-redesign-plan.md).
 *
 * One file per area so the redesign's parallel work never edits the same
 * dictionary. `en.ts` and `es.ts` spread these in; the Spanish half is typed
 * against the English one, so a key missing here is a build failure exactly as
 * it is in the main catalog.
 *
 * The list-and-editor tab (B4) and the upload from the operator's computer
 * (B7). Neutral pronouns throughout — "they", "the owner" — because the console
 * cannot know how an owner is referred to. The field labels and the per-item
 * screen-reader names the tab shares with the old card stay in `en.ts`.
 */
export const catalogEn = {
  /* The list */
  'catalog.list.what': 'what they sell',
  'catalog.list.title': ({ kind, count }: { kind: string; count: number }) =>
    `${kind} · ${String(count)}`,
  'catalog.list.nothing': 'Nothing for sale yet',
  'catalog.list.empty':
    'Nothing for sale yet. Seed the catalog from a file, or add things one at a time.',
  'catalog.list.kinds': 'Kinds of thing they sell',
  'catalog.list.add': 'Add',
  'catalog.list.more': 'More',
  'catalog.list.noHeading': 'Under no heading',
  'catalog.list.lengths': ({ lengths }: { lengths: string }) => `${lengths} min`,
  'catalog.list.minutes': ({ minutes }: { minutes: number }) => `${String(minutes)} min`,
  'catalog.list.sleeps': ({ n }: { n: number }) => `sleeps ${String(n)}`,
  'catalog.list.from': ({ price }: { price: string }) => `from ${price}`,
  'catalog.list.openPrice': 'priced at the counter',
  'catalog.list.attachOnly': 'only added to others',
  'catalog.list.soldOut': 'sold out today',
  'catalog.list.notOnSale': 'not on sale',

  /* The fence, said once at the top of the editor */
  'catalog.fence.settingUp':
    'Prices, names and lengths become the owner’s to change once they are live. While the business is being set up, you can edit everything here.',
  'catalog.fence.liveNow':
    'This business is live, so prices, names, lengths and whether something is sold are the owner’s now, changed in their own app. The words, headings, order and photographs are still yours to fix.',

  /* The editor */
  'catalog.editor.onSale': 'On sale',
  'catalog.editor.notOnSale': 'Not on sale',
  'catalog.editor.ownersNow': 'The owner’s now that they are live.',
  'catalog.editor.headingHint': 'The group it appears under on their site. Blank means no group.',
  'catalog.editor.description': 'Description on their site',
  'catalog.editor.discard': 'Discard changes',
  'catalog.editor.stopSelling': 'Stop selling',
  'catalog.editor.sellAgain': 'Sell it again',
  'catalog.editor.noPhotos': 'None yet',
  'catalog.editor.photoCount': ({ count }: { count: number }) =>
    count === 1 ? '1 photograph' : `${String(count)} photographs`,
  'catalog.editor.editPhotos': 'Add or change photographs',
  'catalog.editor.closePhotos': 'Close photographs',

  /* Lengths */
  'catalog.lengths.note':
    'One treatment sold at three lengths is one thing with three prices, not three things. The marked one is what their site and the AI agent quote, and its price and minutes become the item’s own.',
  'catalog.lengths.fenced':
    'The owner’s now. Lengths and add-ons are priced from their own staff app once they are live.',
  'catalog.lengths.empty': 'Sold one way. Add a length and it becomes the one their site quotes.',
  'catalog.lengths.refHint': 'What the catalog file calls it — 30, 90, large. Guests never see it.',
  'catalog.lengths.sameLength': 'same',
  'catalog.lengths.actions': 'Order and save',

  /* Add-ons */
  'catalog.extras.note':
    'Another of their services, sold attached to this one. What it costs attached is its own number and may be lower than it costs alone.',
  'catalog.extras.attached': ({ price, minutes }: { price: string; minutes: number }) =>
    `${price} attached, +${String(minutes)} min`,
  'catalog.extras.add': 'Link an add-on',
  'catalog.extras.pickLabel': 'Another of their services',
  'catalog.extras.pickOf': ({ item }: { item: string }) =>
    `Add another of their services to ${item}`,

  /* Photographs */
  'catalog.photos.altHint': 'Read aloud to a guest who cannot see it. Required.',
  'catalog.photos.fromIntake': 'From the owner’s own photographs',
  'catalog.photos.fromIntakeHint':
    'The ones they sent through their questionnaire and you have already put on the site. Pictures taken of this very thing come first.',
  'catalog.photos.otherPhotos': 'Everything else they sent',
  'catalog.photos.pasteHint': 'An https address of a picture that is already public.',
  'catalog.upload.label': 'Upload from this computer',
  'catalog.upload.hint':
    'JPEG, PNG or WebP, up to 10 MB each. Uploaded pictures join the list below; Save photographs puts them on the item.',
  'catalog.upload.off':
    'Uploading from this computer is not switched on in this environment: it needs a private place to receive the file and a public address to serve it from. Paste an address instead.',
  'catalog.upload.heic': ({ file }: { file: string }) =>
    `${file} is HEIC, which half the browsers in the world will not show. Convert it to JPEG and upload the JPEG.`,
  'catalog.upload.notPicture': ({ file }: { file: string }) =>
    `${file} is not a JPEG, PNG or WebP picture, so it was not sent.`,
  'catalog.upload.tooBig': ({ file }: { file: string }) =>
    `${file} is larger than 10 MB. Export a smaller copy — a page does not need more.`,
  'catalog.upload.progress': ({ done, of }: { done: number; of: number }) =>
    `Uploading ${String(done)} of ${String(of)}…`,

  /* Confirmations */
  'catalog.confirm.saveTitle': ({ item }: { item: string }) => `Save ${item}?`,
  'catalog.confirm.saveLive':
    'This business is live. Guests read the change on their site, and the AI agent describes it, as soon as it saves.',
  'catalog.confirm.saveVerb': 'Save it',
  'catalog.confirm.stopTitle': ({ item }: { item: string }) => `Stop selling ${item}?`,
  'catalog.confirm.stop':
    'It leaves their site and the AI agent stops offering it. Nothing already booked changes, and past sales still show on their reports. You can sell it again later.',
  'catalog.confirm.againTitle': ({ item }: { item: string }) => `Sell ${item} again?`,
  'catalog.confirm.again':
    'It comes back on their site at its current price, and the AI agent can offer it again.',

  /* The tools that open from the list's header */
  'catalog.tools.close': 'Close',
  'catalog.tools.noHeadings': 'Nothing is filed under a heading yet.',
  'catalog.tools.renameHint': 'Change a heading on everything filed under it',
  'catalog.tools.seedHint': 'Paste a catalog file, dry-run it, then seed',
  'catalog.tools.seedHintLive': 'Refused once a business is live — here is why',
  'catalog.tools.seedLive':
    'This business is live, so a seed is refused here. Re-seeding a working catalog is an operator’s job at a terminal, with the owner watching.',
  'catalog.tools.seedFileHint':
    'Paste it, or press “Seed this” on the owner’s menu in the Website tab.',

  /* Rooms and sailings */
  'catalog.vertical.fileIsTheDoor':
    'Rooms and sailings are shown here and written from the catalog file — the file is the door, and a re-seed updates rather than doubles.',
  'catalog.vertical.noDetails':
    'This environment cannot read room and sailing details yet, so only the name, price and whether it is sold show.',

  /* What goes on their website (B10) */
  'catalog.website.show': 'Show on their website',
  'catalog.website.productHint':
    'Off keeps it on sale at the counter and takes it off their shop page. Allowed at any time.',
  'catalog.website.serviceHint':
    'Off keeps it on the staff menu; guests cannot see or book it on their site.',
  'catalog.website.off': 'Not on their website',
  'catalog.website.photo': 'On their website',
  'catalog.website.photoOf': ({ position }: { position: number }) =>
    `Photo ${String(position)} on their website`,
};

export const catalogEs: typeof catalogEn = {
  'catalog.list.what': 'lo que venden',
  'catalog.list.title': ({ kind, count }: { kind: string; count: number }) =>
    `${kind} · ${String(count)}`,
  'catalog.list.nothing': 'Todavía no hay nada a la venta',
  'catalog.list.empty':
    'Todavía no hay nada a la venta. Siembre el catálogo desde un archivo, o añada las cosas una a una.',
  'catalog.list.kinds': 'Tipos de cosas que venden',
  'catalog.list.add': 'Añadir',
  'catalog.list.more': 'Más',
  'catalog.list.noHeading': 'Sin encabezado',
  'catalog.list.lengths': ({ lengths }: { lengths: string }) => `${lengths} min`,
  'catalog.list.minutes': ({ minutes }: { minutes: number }) => `${String(minutes)} min`,
  'catalog.list.sleeps': ({ n }: { n: number }) => `para ${String(n)}`,
  'catalog.list.from': ({ price }: { price: string }) => `desde ${price}`,
  'catalog.list.openPrice': 'precio en el mostrador',
  'catalog.list.attachOnly': 'solo como añadido',
  'catalog.list.soldOut': 'agotado hoy',
  'catalog.list.notOnSale': 'no está a la venta',

  'catalog.fence.settingUp':
    'Los precios, los nombres y las duraciones pasan a ser del propietario cuando el negocio esté en marcha. Mientras se configura, aquí puede editarlo todo.',
  'catalog.fence.liveNow':
    'Este negocio ya está en marcha, así que los precios, los nombres, las duraciones y si algo se vende son ahora del propietario, que los cambia en su propia app. Los textos, los encabezados, el orden y las fotografías siguen siendo suyos para corregir.',

  'catalog.editor.onSale': 'A la venta',
  'catalog.editor.notOnSale': 'No está a la venta',
  'catalog.editor.ownersNow': 'Es del propietario ahora que está en marcha.',
  'catalog.editor.headingHint':
    'El grupo bajo el que aparece en su sitio. En blanco significa sin grupo.',
  'catalog.editor.description': 'Descripción en su sitio',
  'catalog.editor.discard': 'Descartar cambios',
  'catalog.editor.stopSelling': 'Dejar de vender',
  'catalog.editor.sellAgain': 'Volver a vender',
  'catalog.editor.noPhotos': 'Ninguna todavía',
  'catalog.editor.photoCount': ({ count }: { count: number }) =>
    count === 1 ? '1 fotografía' : `${String(count)} fotografías`,
  'catalog.editor.editPhotos': 'Añadir o cambiar fotografías',
  'catalog.editor.closePhotos': 'Cerrar fotografías',

  'catalog.lengths.note':
    'Un tratamiento que se vende en tres duraciones es una cosa con tres precios, no tres cosas. La marcada es la que citan su sitio y el agente de IA, y su precio y sus minutos pasan a ser los de la cosa.',
  'catalog.lengths.fenced':
    'Es del propietario. Las duraciones y los añadidos se ponen precio desde su propia app del personal cuando está en marcha.',
  'catalog.lengths.empty':
    'Se vende de una sola forma. Añada una duración y pasará a ser la que cita su sitio.',
  'catalog.lengths.refHint':
    'Cómo la llama el archivo del catálogo — 30, 90, grande. Los clientes nunca la ven.',
  'catalog.lengths.sameLength': 'igual',
  'catalog.lengths.actions': 'Orden y guardar',

  'catalog.extras.note':
    'Otro de sus servicios, vendido junto a este. Lo que cuesta como añadido es un número propio y puede ser menor que lo que cuesta solo.',
  'catalog.extras.attached': ({ price, minutes }: { price: string; minutes: number }) =>
    `${price} como añadido, +${String(minutes)} min`,
  'catalog.extras.add': 'Vincular un añadido',
  'catalog.extras.pickLabel': 'Otro de sus servicios',
  'catalog.extras.pickOf': ({ item }: { item: string }) => `Añadir otro de sus servicios a ${item}`,

  'catalog.photos.altHint': 'Se lee en voz alta a un cliente que no puede verla. Obligatorio.',
  'catalog.photos.fromIntake': 'De las fotografías del propietario',
  'catalog.photos.fromIntakeHint':
    'Las que envió en su cuestionario y usted ya puso en el sitio. Las fotos de esta misma cosa aparecen primero.',
  'catalog.photos.otherPhotos': 'Todo lo demás que envió',
  'catalog.photos.pasteHint': 'Una dirección https de una imagen que ya es pública.',
  'catalog.upload.label': 'Subir desde este ordenador',
  'catalog.upload.hint':
    'JPEG, PNG o WebP, hasta 10 MB cada una. Las imágenes subidas se suman a la lista de abajo; Guardar fotografías las pone en la cosa.',
  'catalog.upload.off':
    'Subir desde este ordenador no está activado en este entorno: necesita un lugar privado donde recibir el archivo y una dirección pública desde donde servirlo. Pegue una dirección en su lugar.',
  'catalog.upload.heic': ({ file }: { file: string }) =>
    `${file} es HEIC, que la mitad de los navegadores del mundo no muestran. Conviértala a JPEG y suba el JPEG.`,
  'catalog.upload.notPicture': ({ file }: { file: string }) =>
    `${file} no es una imagen JPEG, PNG o WebP, así que no se envió.`,
  'catalog.upload.tooBig': ({ file }: { file: string }) =>
    `${file} pesa más de 10 MB. Exporte una copia más pequeña — una página no necesita más.`,
  'catalog.upload.progress': ({ done, of }: { done: number; of: number }) =>
    `Subiendo ${String(done)} de ${String(of)}…`,

  'catalog.confirm.saveTitle': ({ item }: { item: string }) => `¿Guardar ${item}?`,
  'catalog.confirm.saveLive':
    'Este negocio está en marcha. Los clientes leen el cambio en su sitio, y el agente de IA lo describe, en cuanto se guarda.',
  'catalog.confirm.saveVerb': 'Guardarlo',
  'catalog.confirm.stopTitle': ({ item }: { item: string }) => `¿Dejar de vender ${item}?`,
  'catalog.confirm.stop':
    'Sale de su sitio y el agente de IA deja de ofrecerlo. Nada de lo ya reservado cambia, y las ventas pasadas siguen en sus informes. Puede volver a venderlo más adelante.',
  'catalog.confirm.againTitle': ({ item }: { item: string }) => `¿Volver a vender ${item}?`,
  'catalog.confirm.again':
    'Vuelve a su sitio con su precio actual, y el agente de IA puede ofrecerlo de nuevo.',

  'catalog.tools.close': 'Cerrar',
  'catalog.tools.noHeadings': 'Todavía no hay nada archivado bajo un encabezado.',
  'catalog.tools.renameHint': 'Cambiar un encabezado en todo lo archivado bajo él',
  'catalog.tools.seedHint': 'Pegue un archivo de catálogo, pruébelo en seco y siémbrelo',
  'catalog.tools.seedHintLive': 'Se rechaza cuando un negocio está en marcha — aquí el porqué',
  'catalog.tools.seedLive':
    'Este negocio está en marcha, así que aquí se rechaza una siembra. Volver a sembrar un catálogo en uso es trabajo de un operador en una terminal, con el propietario delante.',
  'catalog.tools.seedFileHint':
    'Péguelo, o pulse «Sembrar esto» en el menú del propietario, en la pestaña Sitio web.',

  'catalog.vertical.fileIsTheDoor':
    'Las habitaciones y las salidas se muestran aquí y se escriben desde el archivo del catálogo — el archivo es la puerta, y volver a sembrarlo actualiza en lugar de duplicar.',
  'catalog.vertical.noDetails':
    'Este entorno todavía no puede leer los detalles de habitaciones y salidas, así que solo se ven el nombre, el precio y si se vende.',

  'catalog.website.show': 'Mostrar en su sitio web',
  'catalog.website.productHint':
    'Apagado, se sigue vendiendo en el mostrador y desaparece de la tienda de su sitio. Se permite en cualquier momento.',
  'catalog.website.serviceHint':
    'Apagado, sigue en el menú del personal; los clientes no lo ven ni lo reservan en su sitio.',
  'catalog.website.off': 'No está en su sitio web',
  'catalog.website.photo': 'En su sitio web',
  'catalog.website.photoOf': ({ position }: { position: number }) =>
    `Foto ${String(position)} en su sitio web`,
};
